import { fg } from '@atlaskit/platform-feature-flags';
let config;
export const DEFAULT_TTVC_REVISION = 'fy25.03';

// Defensively typed, since this is directly user-editable
// and they could delete empty members

export function setUFOConfig(newConfig) {
  var _newConfig$vc;
  // Handle edge cases with `enabledVCRevisions`
  const {
    enabledVCRevisions
  } = (_newConfig$vc = newConfig === null || newConfig === void 0 ? void 0 : newConfig.vc) !== null && _newConfig$vc !== void 0 ? _newConfig$vc : {};
  if (typeof (enabledVCRevisions === null || enabledVCRevisions === void 0 ? void 0 : enabledVCRevisions.byExperience) === 'object') {
    config = {
      ...newConfig,
      vc: {
        ...newConfig.vc,
        enabledVCRevisions: {
          // enforce axiom about `enabledVCRevisions.all` config
          all: Array.from(new Set([DEFAULT_TTVC_REVISION, ...(enabledVCRevisions === null || enabledVCRevisions === void 0 ? void 0 : enabledVCRevisions.all), ...Object.values(enabledVCRevisions === null || enabledVCRevisions === void 0 ? void 0 : enabledVCRevisions.byExperience).flat()])),
          byExperience: {
            ...(enabledVCRevisions === null || enabledVCRevisions === void 0 ? void 0 : enabledVCRevisions.byExperience)
          }
        }
      }
    };
  } else {
    config = newConfig;
  }
}
export function getConfig() {
  return config;
}
const isValidConfigArray = array => {
  return Array.isArray(array) && array.length > 0;
};
export function getEnabledVCRevisions(experienceKey = '') {
  try {
    var _config$vc;
    if (!config) {
      return [];
    }
    if ((_config$vc = config.vc) !== null && _config$vc !== void 0 && _config$vc.enabled) {
      var _config$vc2, _enabledVCRevisions$b;
      const {
        enabledVCRevisions
      } = (_config$vc2 = config.vc) !== null && _config$vc2 !== void 0 ? _config$vc2 : {};
      if (isValidConfigArray(enabledVCRevisions === null || enabledVCRevisions === void 0 ? void 0 : (_enabledVCRevisions$b = enabledVCRevisions.byExperience) === null || _enabledVCRevisions$b === void 0 ? void 0 : _enabledVCRevisions$b[experienceKey])) {
        var _enabledVCRevisions$b2;
        return (_enabledVCRevisions$b2 = enabledVCRevisions.byExperience) === null || _enabledVCRevisions$b2 === void 0 ? void 0 : _enabledVCRevisions$b2[experienceKey];
      }
      if (isValidConfigArray(enabledVCRevisions === null || enabledVCRevisions === void 0 ? void 0 : enabledVCRevisions.all)) {
        return enabledVCRevisions.all;
      }
      return [DEFAULT_TTVC_REVISION];
    }
    return [];
  } catch {
    return [];
  }
}
export function isVCRevisionEnabled(revision, experienceKey) {
  return getEnabledVCRevisions(experienceKey).includes(revision);
}
export function getMostRecentVCRevision(experienceKey = '') {
  const enabledVCRevisions = getEnabledVCRevisions(experienceKey);
  return enabledVCRevisions[enabledVCRevisions.length - 1];
}
export function getReactHydrationStats() {
  if (!config) {
    return undefined;
  }
  try {
    var _config, _config$getReactHydra;
    const stats = (_config = config) === null || _config === void 0 ? void 0 : (_config$getReactHydra = _config.getReactHydrationStats) === null || _config$getReactHydra === void 0 ? void 0 : _config$getReactHydra.call(_config);
    return stats !== null && stats !== void 0 ? stats : undefined;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('getReactHydrationStats threw an error: ', e);
    return undefined;
  }
}
export function getInteractionRate(name, interactionKind) {
  try {
    if (!config) {
      return 0;
    }
    const {
      killswitch,
      rates,
      rules,
      kind,
      autoGeneratedRate
    } = config;
    if (killswitch != null) {
      // Specifically kill certain events
      if (killswitch.includes(name)) {
        return 0;
      }
    }
    if (rates != null) {
      // Decide the rate explicitly
      if (rates[name] != null) {
        return rates[name];
      }
    }
    if (rules != null) {
      for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];
        const {
          test,
          rate
        } = rule;
        if (test != null && rate != null) {
          const regexp = new RegExp(test, 'ig');
          if (regexp.test(name)) {
            return rate;
          }
        }
      }
    }
    const isAutogenerated = name === null || name === void 0 ? void 0 : name.startsWith('auto-generated');
    if (isAutogenerated) {
      const rate = autoGeneratedRate;
      if (rate != null) {
        return rate;
      }
    }
    if (kind != null) {
      const rate = kind[interactionKind];
      if (rate != null) {
        return rate;
      }
    }
    return 0;
  } catch (e) {
    // Fallback
    return 0;
  }
}
export function getExperimentalInteractionRate(name, interactionType) {
  try {
    if (!config) {
      return 0;
    }
    const {
      experimentalInteractionMetrics
    } = config;
    if (!(experimentalInteractionMetrics !== null && experimentalInteractionMetrics !== void 0 && experimentalInteractionMetrics.enabled)) {
      return 0;
    }
    if (experimentalInteractionMetrics.rates && typeof experimentalInteractionMetrics.rates[name] === 'number') {
      return experimentalInteractionMetrics.rates[name];
    }
    if (experimentalInteractionMetrics.kind && typeof experimentalInteractionMetrics.kind[interactionType] === 'number') {
      return experimentalInteractionMetrics.kind[interactionType];
    }
    return 0;
  } catch (e) {
    return 0;
  }
}
export function getPostInteractionRate(name, interactionType) {
  return getConfigRate(name, interactionType, 'postInteractionLog');
}
export function getCapabilityRate(capability) {
  if (fg('platform_ufo_remove_deprecated_config_fields')) {
    return 1;
  }
  try {
    if (!config) {
      return 0;
    }
    const {
      capability: capabilityRate
    } = config;
    if (capabilityRate != null) {
      const rate = capabilityRate[capability];
      if (rate != null) {
        return rate;
      }
    }
    return 0;
  } catch {
    return 0;
  }
}
function getConfigRate(name, interactionType, configName) {
  try {
    if (!config) {
      return 0;
    }
    const gotConfig = config[configName];
    if (!(gotConfig !== null && gotConfig !== void 0 && gotConfig.enabled)) {
      return 0;
    }
    if (interactionType !== 'page_load' && interactionType !== 'transition') {
      return 0;
    }
    if (gotConfig.rates && typeof gotConfig.rates[name] === 'number') {
      return gotConfig.rates[name];
    }
    if ('kind' in gotConfig && gotConfig.kind && typeof gotConfig.kind[interactionType] === 'number') {
      return gotConfig.kind[interactionType];
    }
    return 0;
  } catch (e) {
    return 0;
  }
}
export function getExtraInteractionRate(name, interactionType) {
  return getConfigRate(name, interactionType, 'extraInteractionMetrics');
}
const validTypingMethods = ['timeout', 'timeoutNoAlloc', 'mutationObserver'];
export function getTypingPerformanceTracingMethod() {
  const defaultMethod = 'timeout';
  try {
    if (!config) {
      return defaultMethod;
    }
    const {
      typingMethod
    } = config;
    if (typingMethod != null && validTypingMethods.find(m => m === typingMethod)) {
      return typingMethod;
    } else {
      return defaultMethod;
    }
  } catch (e) {
    return defaultMethod;
  }
}

// Will ask UFO to wait for BM3 TTI for all events on this list
export function getAwaitBM3TTIList() {
  try {
    if (!config) {
      return [];
    }
    const {
      awaitBM3TTI
    } = config;
    if (awaitBM3TTI != null) {
      return awaitBM3TTI;
    } else {
      return [];
    }
  } catch (e) {
    return [];
  }
}

// Will ask UFO to override ufoName => experienceKey to the apdex key
// This is to cover use cases where one rUFO route is split into multiple BM3 keys (1:N)
// This workaround should only be used as last resource,
// when we find router limitations preventing teams from splliting these routes
export function getUfoNameOverrides() {
  try {
    if (!config) {
      return undefined;
    }
    const {
      ufoNameOverrides
    } = config;
    if (ufoNameOverrides != null) {
      return ufoNameOverrides;
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
}

// Contains the list of interactions that do not abort existing known interactions
export function getDoNotAbortActivePressInteraction() {
  try {
    if (!config) {
      return undefined;
    }
    const {
      doNotAbortActivePressInteraction
    } = config;
    return doNotAbortActivePressInteraction;
  } catch (e) {
    return undefined;
  }
}

// Contains the list of interactions that do not abort on transitions
export function getDoNotAbortActivePressInteractionOnTransition() {
  try {
    if (!config) {
      return undefined;
    }
    const {
      doNotAbortActivePressInteractionOnTransition
    } = config;
    return doNotAbortActivePressInteractionOnTransition;
  } catch (e) {
    return undefined;
  }
}
export const CLEANUP_TIMEOUT = 60 * 1000;
export function getInteractionTimeout(ufoName) {
  try {
    if (!config) {
      return CLEANUP_TIMEOUT;
    }
    const {
      interactionTimeout
    } = config;
    if (interactionTimeout != null && interactionTimeout[ufoName] != null) {
      return interactionTimeout[ufoName];
    }
    if (interactionTimeout != null && interactionTimeout.__globalInteractionTimeout != null) {
      return interactionTimeout.__globalInteractionTimeout;
    } else {
      return CLEANUP_TIMEOUT;
    }
  } catch (e) {
    return CLEANUP_TIMEOUT;
  }
}